import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  inject
} from "@angular/core";
import { CommonModule } from "@angular/common";
import * as Plotly from "plotly.js-dist-min";
import { Subject, combineLatest } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { PlotlyModule } from "angular-plotly.js";
import { MessageModule } from "primeng/message";
import { PanelModule } from "primeng/panel";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import _ from "lodash";

import {
  MzTabResult,
  MzTabResultControllerService
} from "../../../../modules/lipidcompass-backend-client";
import {
  lipidCategoryCompositionPlotLayout,
  lipidCategoryConcentrationPlotLayout,
  lipidClassRangesPlotConfig,
  lipidClassRangesPlotLayout,
  lipidLevelCompositionSunburstConcentrationPlotLayout,
  lipidLevelCompositionSunburstCountPlotLayout,
  lipidSpeciesRangesPlotConfig,
  lipidSpeciesRangesPlotLayout,
  plotlyConfig
} from "../../../environments/environment";
import { LipidLevel } from "../../_models/lipidLevel";
import { PlotService } from "../../_services/plot.service";
import { LoggingService, LogLevel } from "../../_services/logging.service";
import { DatasetComparisonSelectionService } from "../../_services/dataset-comparison-selection.service";
import {
  deriveVisibleClassesAndCategories,
  filterScatterTracesByLabel,
  filterSunburstTrace
} from "../../_services/plot-filter.util";
import { DatasetLipidFiltersComponent } from "../dataset-lipid-filters/dataset-lipid-filters.component";
import { DatasetPlotBoxplotComponent } from "../dataset-plot-boxplot/dataset-plot-boxplot.component";
import { DatasetPlotDonutComponent } from "../dataset-plot-donut/dataset-plot-donut.component";
import {
  ResponsiveTabsComponent,
  ResponsiveTab
} from "../../shared/responsive-tabs/responsive-tabs.component";
import {
  LandscapeFitTabComponent,
  LandscapeSelectableDataset
} from "../../landscape/containers/landscape-fit-tab.component";

/** Landscape fitting runs against PUBLIC composition data only — see
 *  AtlasService.fitDataset, which passes an empty reviewer scope. */
const LANDSCAPE_PUBLIC_ONLY_HINT = "Landscape fitting is only available for public datasets";

/**
 * The composition plots on the dataset detail page: the donut/range grid, the
 * two sunbursts and the species-level boxplot, together with the Lipid Filters
 * panel that scopes them all. Split out of DatasetCardComponent so that
 * component is once again just the dataset card.
 */
@Component({
  selector: "app-dataset-detail-plots",
  templateUrl: "./dataset-detail-plots.component.html",
  styleUrls: ["./dataset-detail-plots.component.css"],
  standalone: true,
  imports: [
    LandscapeFitTabComponent,
    CommonModule,
    DatasetLipidFiltersComponent,
    DatasetPlotBoxplotComponent,
    DatasetPlotDonutComponent,
    MessageModule,
    PanelModule,
    PlotlyModule,
    ProgressSpinnerModule,
    ResponsiveTabsComponent
  ]
})
export class DatasetDetailPlotsComponent implements OnChanges, OnInit, OnDestroy {
  private plotService = inject(PlotService);
  private loggingService = inject(LoggingService);
  private selectionService = inject(DatasetComparisonSelectionService);
  private mzTabResultService = inject(MzTabResultControllerService);
  private destroy$ = new Subject<void>();

  @Input() mzTabResult: MzTabResult;

  /** The mzTabResult id the lipid hierarchy was last requested for. Keyed on
   * the dataset id rather than a one-shot boolean: unlike the Dataset
   * Compare view (whose dataset set only grows), this page genuinely
   * switches datasets — the same component instance survives navigation
   * between two dataset detail pages (see the datasetIds comment below) — so
   * a repeat `mzTabResult` change for a NEW dataset must re-request, while a
   * repeat change for the SAME dataset must not duplicate the request. */
  private lipidHierarchyRequestedFor: string | null = null;

  loadingLipidClassCompositionPlotData: Boolean = false;
  loadingLipidSpeciesCompositionPlotData: Boolean = false;
  loadingLipidSpeciesSunburstCountPlotData: Boolean = false;
  loadingLipidSpeciesSunburstConcentrationPlotData: Boolean = false;

  lipidClassCompositionPlotData: Plotly.Data[] = [];
  lipidSpeciesCompositionPlotData: Plotly.Data[] = [];
  lipidSpeciesSunburstCountPlotData: Plotly.Data[] = [];
  lipidSpeciesSunburstConcentrationPlotData: Plotly.Data[] = [];

  /** Raw unfiltered traces — populated once when each plot's data loads,
   * re-filtered into the fields above whenever the shared lipid filter
   * selection (from the Species Level Boxplot table's Apply/Clear Selection,
   * or the Lipid-Specific Filters panel) changes. */
  private rawLipidClassCompositionPlotData: Plotly.Data[] = [];
  private rawLipidSpeciesCompositionPlotData: Plotly.Data[] = [];
  private rawLipidSpeciesSunburstCountPlotData: Plotly.Data[] = [];
  private rawLipidSpeciesSunburstConcentrationPlotData: Plotly.Data[] = [];

  /** Categories to show in the two donuts (empty = show all). */
  selectedCategories: string[] = [];
  /** True whenever a lipid filter (category/class/species intersection) is active. */
  filterActive: boolean = false;

  categoryLipidLevel: LipidLevel = LipidLevel.CATEGORY;
  speciesLipidLevel: LipidLevel = LipidLevel.SPECIES;

  datasetIds: string[] = [];
  datasetIdsToLabels: Map<string, string> = new Map<string, string>();
  quantificationUnit: string = null;

  /** Index of the tab currently on screen. */
  selectedTabIndex = 0;

  /** Labels and order mirror the Dataset Compare view so the two read the same. */
  private readonly baseTabs: ResponsiveTab[] = [
    { value: 0, label: "Category & Class Level" },
    { value: 1, label: "Species Level - Sunburst" },
    { value: 2, label: "Species Level - Boxplot" },
    { value: 3, label: "Landscape" }
  ];

  /** Rebuilt by refreshTabs() rather than computed by a getter: the child
   * ResponsiveTabsComponent runs cdr.detectChanges() from its own ngOnChanges,
   * so handing it a fresh array on every check would re-enter that path on
   * every change-detection cycle. */
  responsiveTabs: ResponsiveTab[] = this.baseTabs.map(tab => ({ ...tab }));

  /** The single dataset offered to the Landscape fit tab, which fits it without a
   *  selector. `mzTabResultId` carries the nativeId, because that is what
   *  AtlasService.fitDataset resolves — the same value the compare view sends. */
  landscapeDatasets: LandscapeSelectableDataset[] = [];

  /** Whether this dataset can be fitted against the Landscape at all. */
  landscapeAvailable = false;

  public lipidClassRangesPlotLayout: Partial<
    Plotly.Layout
  > = lipidClassRangesPlotLayout;
  public lipidCategoryCompositionPlotLayout: Partial<
    Plotly.Layout
  > = lipidCategoryCompositionPlotLayout;
  public lipidCategoryConcentrationPlotLayout: Partial<
    Plotly.Layout
  > = lipidCategoryConcentrationPlotLayout;
  public lipidSpeciesRangesPlotLayout: Partial<
    Plotly.Layout
  > = lipidSpeciesRangesPlotLayout;
  public lipidLevelCompositionSunburstCountPlotLayout: Partial<
    Plotly.Layout
  > = lipidLevelCompositionSunburstCountPlotLayout;
  public lipidLevelCompositionSunburstConcentrationPlotLayout: Partial<
    Plotly.Layout
  > = lipidLevelCompositionSunburstConcentrationPlotLayout;

  public homePlotlyConfig: Partial<Plotly.Config> = _.cloneDeep(plotlyConfig);
  public lipidClassRangesPlotConfig: Partial<
    Plotly.Config
  > = lipidClassRangesPlotConfig;
  public lipidSpeciesRangesPlotConfig: Partial<
    Plotly.Config
  > = lipidSpeciesRangesPlotConfig;

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes?.mzTabResult?.currentValue) {
      return;
    }
    const mzTabResultId = changes.mzTabResult.currentValue.id;
    this.selectionService.initialize([mzTabResultId]);

    // Eagerly populate the Lipid Filters panel in the background as soon as
    // the dataset id is known, instead of waiting for the user to visit the
    // Species Level Boxplot tab — previously the only place on this page
    // that called selectionService.addEntry() (DatasetPlotBoxplotComponent),
    // which never mounts until that lazy tab is opened. Mirrors the
    // identical fix already applied to DatasetCompareComponent for the same
    // regression. initialize() above already reset the service's lipid
    // hierarchy for this id, so re-populating right after it is correct.
    if (this.lipidHierarchyRequestedFor !== mzTabResultId) {
      this.lipidHierarchyRequestedFor = mzTabResultId;
      this.mzTabResultService
        .mzTabResultGetLipidNamesForLevels([mzTabResultId], "SPECIES", true)
        // Unlike the four plotService subscriptions below (which only ever
        // assign into fields of a — possibly by-then-dead — component
        // instance, harmless on their own), this one calls
        // selectionService.addEntry() on DatasetComparisonSelectionService,
        // which is providedIn: 'root': shared app-wide state. Without
        // teardown, a slow response from dataset A's request could still
        // land after the user navigated to dataset B (or Dataset Compare) —
        // DatasetDetailComponent reads route params in its constructor, so
        // navigation destroys this instance outright — silently widening B's
        // Lipid Filters options with A's lipids. takeUntil(destroy$) cuts
        // that off at unsubscribe.
        .pipe(takeUntil(this.destroy$))
        .subscribe(response => {
          // Belt-and-braces for the in-flight-same-instance case (e.g. a
          // same-id re-entry racing this response, or a future change that
          // narrows what re-requests): only apply a response that still
          // matches the dataset it was requested for.
          if (this.lipidHierarchyRequestedFor !== mzTabResultId) {
            return;
          }
          // addEntry() mutates the service's BehaviorSubjects, which the
          // child DatasetLipidFiltersComponent renders directly and which
          // this component's own ngOnInit subscription (below) also reads
          // to call applyFilters() — a template-bound-state mutation, same
          // NG0100 risk as every other subscribe callback in this method, so
          // defer to the next microtask for the same reason.
          Promise.resolve().then(() => {
            response.entries?.forEach(e => {
              if (e.lipidCategory && e.lipidClass && e.lipidSpecies) {
                this.selectionService.addEntry(
                  e.lipidCategory,
                  e.lipidClass,
                  e.lipidSpecies
                );
              }
            });
          });
        });
    }

    this.loggingService.log(
      "DatasetDetailPlotsComponent",
      "Loading summary data for " + mzTabResultId,
      LogLevel.DEBUG
    );
    // load class level concentration summary
    this.loadingLipidClassCompositionPlotData = true;
    this.plotService
      .loadLipidLevelConcentrationScatterChartPlotData(
        mzTabResultId,
        LipidLevel.CLASS
      )
      .subscribe((plotData: Plotly.Data[]) => {
        // If the service emits synchronously (e.g. a cached response),
        // assigning these bound fields within the same change-detection
        // cycle that triggered ngOnChanges throws NG0100
        // (ExpressionChangedAfterItHasBeenCheckedError). Defer the state
        // update to the next microtask so it lands in a fresh CD cycle
        // (matches the fix already applied to CrudTableComponent and
        // LipidCardComponent for the same class of bug).
        Promise.resolve().then(() => {
          this.rawLipidClassCompositionPlotData = plotData;
          this.loadingLipidClassCompositionPlotData = false;
          this.applyFilters();
          this.refreshTabs();
        });
      });
    // load species level concentration summary
    this.loadingLipidSpeciesCompositionPlotData = true;
    this.plotService
      .loadLipidLevelConcentrationScatterChartPlotData(
        mzTabResultId,
        LipidLevel.SPECIES
      )
      .subscribe((plotData: Plotly.Data[]) => {
        // See the comment above — same NG0100 risk.
        Promise.resolve().then(() => {
          this.rawLipidSpeciesCompositionPlotData = plotData;
          this.loadingLipidSpeciesCompositionPlotData = false;
          this.applyFilters();
          this.refreshTabs();
        });
      });

    this.loadingLipidSpeciesSunburstCountPlotData = true;
    this.plotService
      .loadLipidLevelCompositionSunburstChartPlotData(
        mzTabResultId,
        LipidLevel.SPECIES,
        true
      )
      .subscribe((plotData: Plotly.Data[]) => {
        // See the comment in the class-level composition subscribe
        // above — same NG0100 risk.
        Promise.resolve().then(() => {
          this.loggingService.log(
            "DatasetDetailPlotsComponent",
            "Sunburst plot data: " + JSON.stringify(plotData),
            LogLevel.DEBUG
          );
          plotData.forEach((value, index, array) => {
            value["labels"][0] = this.mzTabResult?.nativeId;
          });
          this.rawLipidSpeciesSunburstCountPlotData = plotData;
          this.loadingLipidSpeciesSunburstCountPlotData = false;
          this.applyFilters();
          this.refreshTabs();
        });
      });

    this.loadingLipidSpeciesSunburstConcentrationPlotData = true;
    this.plotService
      .loadLipidLevelCompositionSunburstChartPlotData(
        mzTabResultId,
        LipidLevel.SPECIES,
        false
      )
      .subscribe((plotData: Plotly.Data[]) => {
        // See the comment in the class-level composition subscribe
        // above — same NG0100 risk.
        Promise.resolve().then(() => {
          this.loggingService.log(
            "DatasetDetailPlotsComponent",
            "Sunburst plot data: " + JSON.stringify(plotData),
            LogLevel.DEBUG
          );
          plotData.forEach((value, index, array) => {
            value["labels"][0] = this.mzTabResult?.nativeId;
          });
          this.rawLipidSpeciesSunburstConcentrationPlotData = plotData;
          this.loadingLipidSpeciesSunburstConcentrationPlotData = false;
          this.applyFilters();
          this.refreshTabs();
        });
      });

    // Assigned, not pushed: this component survives a route change between two
    // dataset detail pages (the parent re-emits a new mzTabResult into the same
    // instance), so appending would leave the previous dataset's id in place
    // and the boxplot would query both.
    this.datasetIds = [this.mzTabResult?.id];
    this.datasetIdsToLabels = new Map<string, string>([
      [this.mzTabResult?.id, this.mzTabResult?.nativeId]
    ]);
    if (this.mzTabResult?.mzTabSummary?.quantificationUnit?.cv_accession) {
      this.quantificationUnit =
        "QUANT." +
        this.mzTabResult?.mzTabSummary?.quantificationUnit?.cv_accession;
    } else {
      this.quantificationUnit = "QUANT.UNDEFINED";
    }
    this.landscapeAvailable = this.mzTabResult?.visibility === "PUBLIC";
    this.landscapeDatasets = [
      {
        id: this.mzTabResult?.id,
        label: this.mzTabResult?.nativeId,
        kind: "loaded" as const,
        mzTabResultId: this.mzTabResult?.nativeId
      }
    ];
    this.refreshTabs();
  }

  ngOnInit(): void {
    combineLatest([
      this.selectionService.filterActive$,
      this.selectionService.effectiveFilteredSpecies$,
      this.selectionService.selectedLipidClasses$,
      this.selectionService.selectedLipidCategories$
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // All four sources are BehaviorSubjects, so combineLatest emits
        // synchronously on subscribe — i.e. synchronously within this
        // ngOnInit() call, still inside the change-detection cycle Angular
        // is checking. Calling applyFilters() (which mutates several
        // template-bound fields) here directly throws NG0100
        // (ExpressionChangedAfterItHasBeenCheckedError). Defer to the next
        // microtask so it lands in a fresh CD cycle.
        Promise.resolve().then(() => this.applyFilters());
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onTabChange(value: number): void {
    this.selectedTabIndex = value;
  }

  /** Re-derives the tab strip's loading, filter and availability indicators.
   * Called wherever the state it reads changes: after each plot loads, from
   * applyFilters(), and once the quantification unit and visibility are known. */
  private refreshTabs(): void {
    const loading = [
      this.loadingLipidClassCompositionPlotData === true ||
        this.loadingLipidSpeciesCompositionPlotData === true,
      this.loadingLipidSpeciesSunburstCountPlotData === true ||
        this.loadingLipidSpeciesSunburstConcentrationPlotData === true,
      !this.quantificationUnit,
      false // the Landscape tab reports its own progress inside the panel
    ];
    this.responsiveTabs = this.baseTabs.map((tab, index) => ({
      ...tab,
      loading: loading[index],
      // The Landscape fit ignores the lipid filters, exactly as in the compare view.
      filterActive: index < 3 ? this.filterActive : false,
      disabled: index === 3 ? !this.landscapeAvailable : false,
      tooltip: index === 3 && !this.landscapeAvailable ? LANDSCAPE_PUBLIC_ONLY_HINT : undefined
    }));
  }

  /** Re-derives the displayed donut/range-plot/sunburst data from the raw
   * (unfiltered) traces using the current DatasetComparisonSelectionService
   * state — called whenever that state changes, and once right after each
   * raw dataset finishes loading (in case a filter was already active). */
  private applyFilters(): void {
    const active = this.selectionService.filterActive$.value;
    const effSpecies = this.selectionService.effectiveFilteredSpecies$.value;
    const selCls = this.selectionService.selectedLipidClasses$.value;
    const selCats = this.selectionService.selectedLipidCategories$.value;

    this.filterActive = active;

    const { classes, cats } = deriveVisibleClassesAndCategories(
      this.selectionService.allEntries,
      active,
      effSpecies,
      selCls,
      selCats
    );
    this.selectedCategories = cats;
    this.lipidClassCompositionPlotData = filterScatterTracesByLabel(
      this.rawLipidClassCompositionPlotData,
      classes
    );

    const visibleSpecies = active ? new Set(effSpecies) : null;
    this.lipidSpeciesCompositionPlotData = filterScatterTracesByLabel(
      this.rawLipidSpeciesCompositionPlotData,
      visibleSpecies
    );
    this.lipidSpeciesSunburstCountPlotData = this.filterSunburstTraces(
      this.rawLipidSpeciesSunburstCountPlotData,
      visibleSpecies
    );
    this.lipidSpeciesSunburstConcentrationPlotData = this.filterSunburstTraces(
      this.rawLipidSpeciesSunburstConcentrationPlotData,
      visibleSpecies
    );
    this.refreshTabs();
  }

  private filterSunburstTraces(
    rawTraces: Plotly.Data[],
    visibleSpecies: Set<string> | null
  ): Plotly.Data[] {
    if (visibleSpecies === null) return [...rawTraces];
    return rawTraces
      .map(trace => filterSunburstTrace(trace, visibleSpecies))
      .filter(Boolean) as Plotly.Data[];
  }
}
